import React, { useCallback, useState } from "react";
import { Button, Input } from "components/bootstrap";
const DEFAULT_ATTRIBUTES = [
  "sAMAccountName",
  "cn",
  "displayName",
  "givenName",
  "sn",
  "mail",
  "manager",
  "memberOf",
  "ms-DS-Lockout-Duration",
  "msDS-UserAccountDisabled",
  "primarygroupid",
  "telephoneNumber",
  "title",
  "accountExpires"
];
const AVAILABLE_ATTRIBUTES = {
  "Identity / Account Basics": [
    "sAMAccountName",
    "userPrincipalName",
    "displayName",
    "givenName",
    "sn",
    "cn",
    "name",
    "objectGUID",
    "objectSID",
    "distinguishedName",
    "whenCreated",
    "whenChanged"
  ],
  "Organization / Role": [
    "company",
    "department",
    "title",
    "manager",
    "directReports",
    "employeeID",
    "employeeNumber",
    "employeeType"
  ],
  "Contact Info": [
    "mail",
    "telephoneNumber",
    "mobile",
    "homePhone",
    "streetAddress",
    "l",
    "st",
    "postalCode",
    "co",
    "physicalDeliveryOfficeName"
  ],
  "Account Status / Security": [
    "userAccountControl",
    "accountExpires",
    "lastLogonTimestamp",
    "pwdLastSet",
    "badPwdCount",
    "lockoutTime",
    "memberOf",
    "primarygroupid",
    "ms-DS-Lockout-Duration",
    "msDS-UserAccountDisabled"
  ],
  "Directory / Infrastructure": [
    "canonicalName",
    "description",
    "info"
  ],
  "Extension Attributes": [
    "extensionAttribute1",
    "extensionAttribute2",
    "extensionAttribute3",
    "extensionAttribute4",
    "extensionAttribute5",
    "extensionAttribute6",
    "extensionAttribute7",
    "extensionAttribute8",
    "extensionAttribute9",
    "extensionAttribute10",
    "extensionAttribute11",
    "extensionAttribute12",
    "extensionAttribute13",
    "extensionAttribute14",
    "extensionAttribute15"
  ]
};
const LDAPAdapterFieldSet = ({
  config,
  handleFormEvent,
  setFieldValue,
  validationMessage,
  validationState
}) => {
  var _a, _b, _c;
  const [isCreate] = useState(() => {
    var _a2;
    return !((_a2 = config.user_passwd) == null ? void 0 : _a2.keep_value);
  });
  const [showResetPasswordButton, setShowResetPasswordButton] = useState(!!((_a = config.user_passwd) == null ? void 0 : _a.keep_value));
  const [showAttributes, setShowAttributes] = useState(false);
  const setUserPassword = (nextUserPassword) => {
    setFieldValue("config.user_passwd", nextUserPassword);
  };
  const handleUserPasswordChange = ({ target }) => {
    const typedPassword = target.value;
    let nextPassword = { set_value: typedPassword };
    if (typedPassword === "") {
      nextPassword = { delete_value: true };
    }
    setUserPassword(nextPassword);
  };
  const toggleUserPasswordReset = () => {
    if (showResetPasswordButton) {
      setUserPassword({ delete_value: true });
      setShowResetPasswordButton(false);
      return;
    }
    setUserPassword({ keep_value: true });
    setShowResetPasswordButton(true);
  };
  const handleAttributeToggle = useCallback(
    (attribute) => {
      const currentAttrs = config.custom_attributes || [];
      const newAttrs = currentAttrs.includes(attribute) ? currentAttrs.filter((a) => a !== attribute) : [...currentAttrs, attribute];
      setFieldValue("config.custom_attributes", newAttrs);
    },
    [config, setFieldValue]
  );
  const handleSelectAll = useCallback(() => {
    const allAttrs = Object.values(AVAILABLE_ATTRIBUTES).flat();
    setFieldValue("config.custom_attributes", allAttrs);
  }, [setFieldValue]);
  const handleClearAll = useCallback(() => {
    setFieldValue("config.custom_attributes", []);
  }, [setFieldValue]);
  const selectedCount = ((_b = config.custom_attributes) == null ? void 0 : _b.length) || 0;
  return /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "ldap_ip",
      name: "ldap_ip",
      label: "LDAP Server IP Address",
      onChange: handleFormEvent,
      help: validationMessage("ldap_ip", "LDAP server IP address."),
      bsStyle: validationState("ldap_ip"),
      value: config.ldap_ip,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      id: "ldap_connect_timeout",
      name: "ldap_connect_timeout",
      label: "LDAP Connect Timeout",
      required: true,
      onChange: handleFormEvent,
      help: validationMessage("ldap_connect_timeout", "LDAP connection timeout in milliseconds."),
      bsStyle: validationState("ldap_connect_timeout"),
      value: config.ldap_connect_timeout,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      id: "ldap_pool_max_connections",
      name: "ldap_pool_max_connections",
      label: "LDAP Pool Max Connections",
      required: true,
      onChange: handleFormEvent,
      help: validationMessage("ldap_pool_max_connections", "The number of max connections simultaneously."),
      bsStyle: validationState("ldap_pool_max_connections"),
      value: config.ldap_pool_max_connections,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "base_dn",
      name: "base_dn",
      label: "Base DN",
      onChange: handleFormEvent,
      help: validationMessage("base_dn", "The DN of the LDAP server"),
      bsStyle: validationState("base_dn"),
      value: config.base_dn,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "user_dn",
      name: "user_dn",
      label: "User DN",
      onChange: handleFormEvent,
      help: validationMessage(
        "CN=Administrator",
        "The DN of the user who is authorized to bind to the LDAP Server. (e.g CN=Administrator)"
      ),
      bsStyle: validationState("user_dn"),
      value: config.user_dn,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), showResetPasswordButton ? /* @__PURE__ */ React.createElement(Input, { id: "user_passwd", label: "User Password", labelClassName: "col-sm-3", wrapperClassName: "col-sm-9" }, /* @__PURE__ */ React.createElement(Button, { onClick: toggleUserPasswordReset }, "Reset password")) : /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "password",
      id: "user_passwd",
      name: "user_passwd",
      label: "User Password",
      buttonAfter: !isCreate ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: toggleUserPasswordReset }, "Undo Reset") : void 0,
      onChange: handleUserPasswordChange,
      help: validationMessage(
        "user_passwd",
        "The password of the user who is authorized to bind to the LDAP Server."
      ),
      bsStyle: validationState("user_passwd"),
      value: ((_c = config.user_passwd) == null ? void 0 : _c.set_value) || "",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "number",
      id: "ldap_port",
      name: "ldap_port",
      label: "LDAP Port",
      required: true,
      onChange: handleFormEvent,
      help: validationMessage("ldap_port", "The port to bind to the LDAP server."),
      bsStyle: validationState("ldap_port"),
      value: config.ldap_port,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: "tls",
      name: "tls",
      label: "Enable TLS",
      checked: config.tls,
      onChange: handleFormEvent,
      wrapperClassName: "col-md-offset-3 col-md-9"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: "verify_certificate",
      name: "verify_certificate",
      label: "Verify Certificates",
      checked: config.verify_certificate,
      onChange: handleFormEvent,
      wrapperClassName: "col-md-offset-3 col-md-9"
    }
  ), /* @__PURE__ */ React.createElement("div", { className: "form-group" }, /* @__PURE__ */ React.createElement("label", { className: "col-sm-3 control-label", htmlFor: "custom_attributes" }, "Custom Attributes"), /* @__PURE__ */ React.createElement("div", { className: "col-sm-9" }, /* @__PURE__ */ React.createElement("p", { className: "help-block", style: { marginTop: 0 } }, "Select specific attributes to retrieve from Active Directory. The default 14 attributes are pre-selected when opening the selector.", selectedCount > 0 && /* @__PURE__ */ React.createElement("strong", null, " (", selectedCount, " selected)")), /* @__PURE__ */ React.createElement("div", { style: { marginBottom: "8px" } }, /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: () => {
    if (!showAttributes && (!config.custom_attributes || config.custom_attributes.length === 0)) {
      setFieldValue("config.custom_attributes", DEFAULT_ATTRIBUTES);
    }
    setShowAttributes(!showAttributes);
  }, style: { marginRight: "5px" } }, showAttributes ? "Hide Attributes" : "Show Attributes"), showAttributes && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: handleSelectAll, style: { marginRight: "5px" } }, "Select All"), /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: handleClearAll }, "Clear All"))), showAttributes && /* @__PURE__ */ React.createElement(
    "div",
    {
      id: "custom_attributes",
      style: {
        maxHeight: "300px",
        overflowY: "auto",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "10px"
      }
    },
    Object.entries(AVAILABLE_ATTRIBUTES).map(([category, attrs]) => /* @__PURE__ */ React.createElement("div", { key: category, style: { marginBottom: "10px" } }, /* @__PURE__ */ React.createElement("strong", { style: { display: "block", marginBottom: "4px", borderBottom: "1px solid #eee", paddingBottom: "2px" } }, category), /* @__PURE__ */ React.createElement("div", { style: { paddingLeft: "15px", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "2px" } }, attrs.map((attr) => {
      var _a2;
      return /* @__PURE__ */ React.createElement("label", { key: attr, htmlFor: `attr-${attr}`, style: { display: "flex", alignItems: "center", fontWeight: "normal", cursor: "pointer", margin: 0 } }, /* @__PURE__ */ React.createElement(
        "input",
        {
          id: `attr-${attr}`,
          "aria-label": attr,
          type: "checkbox",
          checked: ((_a2 = config.custom_attributes) == null ? void 0 : _a2.includes(attr)) || false,
          onChange: () => handleAttributeToggle(attr),
          style: { marginRight: "5px" }
        }
      ), attr);
    }))))
  ))));
};
export default LDAPAdapterFieldSet;
